import type { FC } from 'react'
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material'
import AssistantRoundedIcon from '@mui/icons-material/AssistantRounded'
import { Markdown } from '~/shared/ui'
import { AnswerFeedback } from './AnswerFeedback'
import { CitationChips } from './CitationChips'
import type { LuciMessage } from './LuciService'
import { citationsOf, isSettled } from './LuciService'
import { ToolTrace, traceOf } from './ToolTrace'

interface LuciTurnsProps {
  messages: LuciMessage[]
  personId: number
  patientId: number | null
  disabled: boolean
  stalled: boolean
  onAsk: (question: string) => Promise<boolean>
  onFollowCitation: () => void
}

export const LuciTurns: FC<LuciTurnsProps> = ({
  messages,
  personId,
  patientId,
  disabled,
  stalled,
  onAsk,
  onFollowCitation,
}) => (
  <Stack spacing={2} data-testid="luci-turns">
    {messages.map((message, index) =>
      message.role === 'staff' ? (
        <StaffTurn key={message.id} message={message} />
      ) : (
        <AssistantTurn
          key={message.id}
          message={message}
          personId={personId}
          patientId={patientId}
          question={questionBefore(messages, index)}
          disabled={disabled}
          stalled={stalled}
          onAsk={onAsk}
          onFollowCitation={onFollowCitation}
        />
      ),
    )}
  </Stack>
)

const questionBefore = (messages: LuciMessage[], index: number): string | null => {
  for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
    const earlier = messages[cursor]
    if (earlier?.role === 'staff' && earlier.content != null) return earlier.content
  }
  return null
}

const StaffTurn: FC<{ message: LuciMessage }> = ({ message }) => (
  <Box
    data-testid="luci-staff-turn"
    sx={theme => ({
      alignSelf: 'flex-end',
      maxWidth: '80%',
      px: 1.5,
      py: 1,
      borderRadius: '12px',
      bgcolor: theme.palette.grey[100],
    })}
  >
    <Typography sx={{ fontSize: '1.4rem', whiteSpace: 'pre-wrap' }}>{message.content}</Typography>
  </Box>
)

interface AssistantTurnProps {
  message: LuciMessage
  personId: number
  patientId: number | null
  question: string | null
  disabled: boolean
  stalled: boolean
  onAsk: (question: string) => Promise<boolean>
  onFollowCitation: () => void
}

const AssistantTurn: FC<AssistantTurnProps> = props => (
  <Stack direction="row" spacing={1.25} data-testid="luci-assistant-turn">
    <AssistantRoundedIcon aria-hidden fontSize="small" sx={{ color: 'text.secondary', mt: 0.5 }} />
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <AssistantBody {...props} />
    </Box>
  </Stack>
)

const AssistantBody: FC<AssistantTurnProps> = ({
  message,
  personId,
  patientId,
  question,
  disabled,
  stalled,
  onAsk,
  onFollowCitation,
}) => {
  if (!isSettled(message.status) && stalled) {
    return (
      <Typography sx={{ color: 'text.secondary', fontSize: '1.4rem' }} data-testid="luci-stalled">
        Luci lost touch with this answer. Reopen the conversation to check on it.
      </Typography>
    )
  }
  const trace = traceOf(message)
  if (!isSettled(message.status) && trace.length > 0) {
    return <ToolTrace entries={trace} settled={false} />
  }
  if (!isSettled(message.status)) {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }} data-testid="luci-working">
        <CircularProgress size={16} />
        <Typography sx={{ color: 'text.secondary', fontSize: '1.4rem' }}>
          Luci is reading the member&rsquo;s record…
        </Typography>
      </Stack>
    )
  }
  if (message.content == null) {
    const retry =
      question == null ? undefined : (
        <Button
          color="inherit"
          size="small"
          disabled={disabled}
          onClick={() => void onAsk(question)}
        >
          Try again
        </Button>
      )
    return (
      <Alert severity="error" action={retry} data-testid="luci-error">
        {message.error_message ?? 'Luci could not answer this question.'}
      </Alert>
    )
  }
  return (
    <>
      <ToolTrace entries={trace} settled />
      <Markdown>{message.content}</Markdown>
      <CitationChips
        citations={citationsOf(message)}
        personId={personId}
        patientId={patientId}
        onFollow={onFollowCitation}
      />
      <AnswerFeedback message={message} personId={personId} />
      {message.suggested_followups.length > 0 && (
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}
          data-testid="luci-followups"
        >
          {message.suggested_followups.map(followup => (
            <Chip
              key={followup}
              size="small"
              label={followup}
              disabled={disabled}
              onClick={() => void onAsk(followup)}
            />
          ))}
        </Stack>
      )}
    </>
  )
}
